import type {
  CrmCategoryRef,
  CrmLabelRef,
  CrmLastActivity,
  CrmLastContact,
  CrmNextAction,
  CrmOrganisationListRef,
  CrmOwner,
} from '../types'

export type OrganisationSort = 'name' | '-name' | 'updated_at' | '-updated_at'
export type OrganisationStatus = 'actief' | 'inactief'
export type MatchReason = 'kvk' | 'website' | 'domain' | 'name' | 'similar'

/** Dutch reason in the "Lijkt op …" warning. */
export const MATCH_REASON_LABELS: Record<MatchReason, string> = {
  kvk: 'zelfde KvK-nummer',
  website: 'zelfde website',
  domain: 'zelfde e-maildomein',
  name: 'zelfde naam',
  similar: 'lijkt op',
}

export interface CrmPersonRef {
  person_id: string
  full_name: string
}

/** GET /crm/organisations items (schemas_organisations.OrganisationListItem). */
export interface CrmOrganisationListItem {
  id: string
  name: string
  website: string | null
  domains: string[]
  city: string | null
  status: OrganisationStatus
  category: CrmCategoryRef | null
  owner: CrmOwner | null
  contacts_count: number
  contacts_preview: CrmPersonRef[]
  updated_at: string
}

export interface CrmOrganisationListOut {
  items: CrmOrganisationListItem[]
  total: number
  page: number
  page_size: number
}

/** GET /crm/organisations/{id} (schemas_organisations.OrganisationDetailOut). */
export interface CrmOrganisation {
  id: string
  name: string
  description: string | null
  website: string | null
  domains: string[]
  kvk_number: string | null
  email: string | null
  phone: string | null
  street: string | null
  house_number: string | null
  postcode: string | null
  city: string | null
  country: string | null
  linkedin_url: string | null
  category: CrmCategoryRef | null
  owner: CrmOwner | null
  status: OrganisationStatus
  summary_html: string | null
  relation_since: string | null
  source: string
  labels: CrmLabelRef[]
  contacts_count: number
  former_count: number
  created_at: string
  updated_at: string
  created_by: CrmOwner | null
  /** spec-63 E3: who last changed "Over …", the last contact, Gepland's first task, lists. */
  summary_updated_at: string | null
  summary_updated_by: CrmOwner | null
  last_contact: CrmLastContact | null
  next_action: CrmNextAction | null
  lists: CrmOrganisationListRef[]
  last_activity?: CrmLastActivity | null
}

/** Body of POST /crm/organisations; PATCH takes the same fields plus `status`. */
export interface CrmOrganisationInput {
  name?: string
  description?: string | null
  website?: string | null
  domains?: string[]
  kvk_number?: string | null
  email?: string | null
  phone?: string | null
  street?: string | null
  house_number?: string | null
  postcode?: string | null
  city?: string | null
  country?: string | null
  linkedin_url?: string | null
  category_id?: string | null
  owner_user_id?: string | null
  status?: OrganisationStatus
  summary_html?: string | null
  relation_since?: string | null
  label_ids?: string[]
  /** spec-63 E4 (ED-33): hand the agreements and open tasks to the new owner too. */
  transfer_agreements?: boolean
  /** spec-63 E4: end the running agreements together with archiving. */
  end_agreements?: boolean
}

export interface CrmOrganisationMatch {
  id: string
  name: string
  city: string | null
  reason: MatchReason
  score: number | null
}

/** A contact person (schemas_organisations.MemberOut). */
export interface CrmMember {
  id: string
  organisation_id: string
  person_id: string
  full_name: string
  email: string | null
  phone: string | null
  role: string | null
  is_primary: boolean
  started_on: string | null
  ended_on: string | null
  owner: CrmOwner | null
  last_contact_at: string | null
}

export interface CrmMemberList {
  items: CrmMember[]
  current_count: number
  former_count: number
}

export interface CrmMemberInput {
  person_id?: string
  new_contact?: { full_name: string; email?: string; phone?: string }
  role?: string | null
  is_primary?: boolean
  started_on?: string | null
  confirm_founder?: boolean
}

export interface CrmMemberPatch {
  role?: string | null
  is_primary?: true
  started_on?: string | null
  ended_on?: string | null
}
